import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import FormGroup from "@mui/material/FormGroup";
import FormHelperText from "@mui/material/FormHelperText";
import Link from "@mui/material/Link";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import { FC, useContext, useMemo } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import * as yup from "yup";
import { useExtendedForm, routes } from "../common";
import { AuthContext } from "./useAuth";
import { AuthPage } from "./components";
import { oauth2Enabled } from "./oauth2/oauth2Config";
import OidcLoginButton from "./oauth2/OidcLoginButton";

const schema = yup
  .object<{ email: string }>()
  .shape({
    email: yup.string().email().required("email is missing"),
  })
  .defined();

const MagicLinkLoginPage: FC = () => {
  const { requestMagicLink } = useContext(AuthContext);
  const location = useLocation();
  const search = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const loginHint = search.get("loginHint") || "";

  const formal = useExtendedForm({
    initialValues: { email: loginHint },
    schema,
    onSubmit: (values: { email: string }) => {
      return requestMagicLink(values.email);
    },
  });

  return (
    <AuthPage>
      {oauth2Enabled && <OidcLoginButton />}
      <form {...formal.getFormProps()} style={{ width: "100%" }}>
        <FormGroup>
          <Typography variant="h5" align="center">
            Sign in
          </Typography>
          {formal.submitState.state === "success" ? (
            <Alert severity="success">
              <AlertTitle>Check your inbox!</AlertTitle>
              <Typography variant="body2">
                Click the sign-in link that was sent to your email.
                <br />
                <br />
                This link will expire in 1 hour.
              </Typography>
            </Alert>
          ) : (
            <>
              <TextField
                label="Email address"
                fullWidth
                type="email"
                {...formal.getTextFieldProps("email")}
              />
              <p>
                <Button
                  variant="contained"
                  size="large"
                  color="primary"
                  fullWidth
                  {...formal.getSubmitButtonProps()}
                  // Enable submit when email is prefilled (loginHint) and not submitting/validating
                  disabled={
                    !formal.values.email ||
                    formal.isSubmitting ||
                    formal.isValidating
                  }
                  type="submit"
                >
                  Send Sign-in Link
                </Button>
                {formal.submitState.state === "error" && (
                  <FormHelperText error>
                    {formal.submitState.message ||
                      "Something went wrong. Please try again."}
                  </FormHelperText>
                )}
              </p>
            </>
          )}
          <Typography align="center" sx={{ fontSize: 12 }}>
            Prefer password login? {""}
            <Link
              component={RouterLink}
              sx={{ color: "#7d7d7d", textDecoration: "underline" }}
              to={
                formal.values.email
                  ? `${routes.USERNAME_PASSWORD_LOGIN.path}?loginHint=${encodeURIComponent(
                      formal.values.email,
                    )}`
                  : routes.USERNAME_PASSWORD_LOGIN.path
              }
            >
              Click here
            </Link>
          </Typography>
          <Divider sx={{ my: 4 }} />
          <Typography variant="body1" align="center">
            No account yet? {""}
            <Link component={RouterLink} to={routes.REGISTER.path}>
              Sign up
            </Link>
          </Typography>
        </FormGroup>
      </form>
    </AuthPage>
  );
};

export default MagicLinkLoginPage;
